import React from "react";

/**
 * Contact-link helpers.
 * - EmailLink → mailto:
 * - PhoneLink → tel: (strips to digits, defaults to US +1)
 * - AddressLink → Google Maps search
 *
 * All accept `className` and `children` overrides.
 */

function normalizePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits;
}

function mapQuery({ address, city, state, zip_code }) {
  const parts = [address, city, state, zip_code].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

export function EmailLink({ email, className = "", children, ...rest }) {
  if (!email) return null;
  return (
    <a href={`mailto:${email}`} className={className || "text-primary underline hover:text-primary/80"} {...rest}>
      {children ?? email}
    </a>
  );
}

export function PhoneLink({ phone, className = "", children, ...rest }) {
  if (!phone) return null;
  const tel = normalizePhone(phone);
  return (
    <a href={`tel:${tel}`} className={className || "underline decoration-white/25 underline-offset-4 hover:text-primary hover:decoration-primary transition-colors"} {...rest}>
      {children ?? phone}
    </a>
  );
}

export function AddressLink({ address, city, state, zip_code, className = "", children, ...rest }) {
  if (!address) return null;
  const href = mapQuery({ address, city, state, zip_code });
  // Use a <span role="link"> so it can be safely nested inside a Router <Link>
  // (nested anchors / buttons are invalid HTML and warn in React). Click + Enter/Space
  // open Google Maps in a new tab.
  const open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, "_blank", "noopener,noreferrer");
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") open(e);
  };
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
      title={address}
      className={className || "underline decoration-white/25 underline-offset-4 hover:text-primary hover:decoration-primary transition-colors cursor-pointer"}
      {...rest}>
      {children ?? address}
    </span>
  );
}
